"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { RiCalendarCheckLine } from "@remixicon/react";
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  isSameMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { toast } from "sonner";

import { cn } from "../../lib/utils";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { CalendarDndProvider } from "./calendar-dnd-context";
import { AgendaView } from "./agenda-view";
import { DayView } from "./day-view";
import { EventDialog } from "./event-dialog";
import { MonthView } from "./month-view";
import { WeekView } from "./week-view";
import {
  AgendaDaysToShow,
  EventGap,
  EventHeight,
  WeekCellsHeight,
  TimeSlotIntervalMinutes,
} from "./constants";

/**
 * @typedef {import("./types").CalendarEvent} CalendarEvent
 * @typedef {import("./types").CalendarView} CalendarView
 */

const VIEW_OPTIONS = [
  { value: "month", label: "Month", shortcut: "M" },
  { value: "week", label: "Week", shortcut: "W" },
  { value: "day", label: "Day", shortcut: "D" },
  { value: "agenda", label: "Agenda", shortcut: "A" },
];

const DAY_GROUPING_OPTIONS = [
  { value: "doctors", label: "Doctors", shortcut: "D" },
  { value: "chairs", label: "Dental chairs", shortcut: "C" },
];

const DOCTORS = [
  {
    id: "dr-simona",
    name: "Dr. Simona Petrauskienė",
    details: "Endodontist",
  },
  {
    id: "dr-tomas",
    name: "Dr. Tomas Kazlauskas",
    details: "Prosthodontist",
  },
  {
    id: "dr-ieva",
    name: "Dr. Ieva Grigaitė",
    details: "Dental hygienist",
  },
];

const DEFAULT_CHAIR_DETAILS = [
  "Surgery suite",
  "Restorative bay",
  "Digital workflow",
];

const createChairs = (count = 3) => {
  const chairsCount = Math.max(1, Math.min(10, Number(count) || 3));
  return Array.from({ length: chairsCount }, (_, index) => {
    const id = `chair-${index + 1}`;
    return {
      id,
      name: `Operatory ${index + 1}`,
      details: DEFAULT_CHAIR_DETAILS[index] || undefined,
    };
  });
};

export function EventCalendar({
  events = [],
  onEventAdd,
  onEventUpdate,
  onEventDelete,
  className,
  initialView = "month",
  calendars = [],
  onToggleSidebar,
  isSidebarOpen,
  currentDate: controlledCurrentDate,
  onCurrentDateChange,
  timezone,
  timeFormat = "24h",
  weekStartsOn = 1,
  businessHours = { startHour: 8, endHour: 20 },
  onViewChange,
  clinicChairs = 3,
}) {
  const isControlled = controlledCurrentDate instanceof Date;
  const [uncontrolledDate, setUncontrolledDate] = useState(
    controlledCurrentDate || new Date()
  );
  const currentDate = isControlled ? controlledCurrentDate : uncontrolledDate;
  const setCurrentDate = (date) => {
    if (!isControlled) {
      setUncontrolledDate(date);
    }
    onCurrentDateChange?.(date);
  };
  const [view, setView] = useState(initialView);
  const updateView = useCallback(
    (nextView) => {
      setView(nextView);
      onViewChange?.(nextView);
    },
    [onViewChange]
  );
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dayGrouping, setDayGrouping] = useState("doctors");
  const chairs = useMemo(() => createChairs(clinicChairs), [clinicChairs]);

  useEffect(() => {
    if (dayGrouping === "chairs" && chairs.length <= 1) {
      setDayGrouping("doctors");
    }
  }, [dayGrouping, chairs]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        isEventDialogOpen ||
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) {
        return;
      }

      const key = e.key.toLowerCase();
      if (key === "m") updateView("month");
      if (key === "w") updateView("week");
      if (key === "d") updateView("day");
      if (key === "a") updateView("agenda");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEventDialogOpen, updateView]);

  const handlePrevious = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (view === "week") {
      setCurrentDate(subWeeks(currentDate, 1));
    } else if (view === "day") {
      setCurrentDate(addDays(currentDate, -1));
    } else if (view === "agenda") {
      setCurrentDate(addDays(currentDate, -AgendaDaysToShow));
    }
  };

  const handleNext = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (view === "week") {
      setCurrentDate(addWeeks(currentDate, 1));
    } else if (view === "day") {
      setCurrentDate(addDays(currentDate, 1));
    } else if (view === "agenda") {
      setCurrentDate(addDays(currentDate, AgendaDaysToShow));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setIsEventDialogOpen(true);
  };

  const handleEventCreate = (startTime, options = {}) => {
    const start = new Date(startTime);
    const minutes = start.getMinutes();
    const interval = TimeSlotIntervalMinutes;
    const rounded = Math.round(minutes / interval) * interval;
    if (rounded === 60) {
      start.setHours(start.getHours() + 1, 0, 0, 0);
    } else {
      start.setMinutes(rounded, 0, 0);
    }

    const newEvent = {
      id: "",
      title: "",
      start,
      end: addHours(start, 1),
      allDay: false,
      calendarId: calendars[0]?.id || "default",
      doctorId: options.doctorId || DOCTORS[0]?.id,
      chairId: options.chairId || chairs[0]?.id || "chair-1",
    };
    setSelectedEvent(newEvent);
    setIsEventDialogOpen(true);
  };

  const handleEventSave = (event) => {
    if (event.id) {
      onEventUpdate?.(event);
      toast(`Event "${event.title}" updated`, {
        description: format(new Date(event.start), "MMM d, yyyy"),
        position: "bottom-left",
      });
    } else {
      const createdEvent = {
        ...event,
        id: Math.random().toString(36).substring(2, 11),
      };
      onEventAdd?.(createdEvent);
      toast(`Event "${event.title}" added`, {
        description: format(new Date(event.start), "MMM d, yyyy"),
        position: "bottom-left",
      });
    }
    setIsEventDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleEventDelete = (eventId) => {
    const deletedEvent = events.find((e) => e.id === eventId);
    onEventDelete?.(eventId);
    setIsEventDialogOpen(false);
    setSelectedEvent(null);

    if (deletedEvent) {
      toast(`Event "${deletedEvent.title}" deleted`, {
        description: format(new Date(deletedEvent.start), "MMM d, yyyy"),
        position: "bottom-left",
      });
    }
  };

  const handleEventUpdate = (updatedEvent) => {
    onEventUpdate?.(updatedEvent);
    toast(`Event "${updatedEvent.title}" moved`, {
      description: format(new Date(updatedEvent.start), "MMM d, yyyy"),
      position: "bottom-left",
    });
  };

  const viewTitle = useMemo(() => {
    if (view === "month") {
      return format(currentDate, "MMMM yyyy");
    }
    if (view === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      if (isSameMonth(start, end)) {
        return format(start, "MMMM yyyy");
      }
      return `${format(start, "MMM")} - ${format(end, "MMM yyyy")}`;
    }
    if (view === "day") {
      return format(currentDate, "EEEE, MMMM d, yyyy");
    }
    if (view === "agenda") {
      const start = currentDate;
      const end = addDays(currentDate, AgendaDaysToShow - 1);

      if (isSameMonth(start, end)) {
        return format(start, "MMMM yyyy");
      }
      return `${format(start, "MMM")} - ${format(end, "MMM yyyy")}`;
    }
    return format(currentDate, "MMMM yyyy");
  }, [currentDate, view]);

  const dayResources = dayGrouping === "chairs" ? chairs : DOCTORS;

  return (
    <div
      className="flex flex-col rounded-lg has-data-[slot=month-view]:flex-1"
      style={{
        "--event-height": `${EventHeight}px`,
        "--event-gap": `${EventGap}px`,
        "--week-cells-height": `${WeekCellsHeight}px`,
      }}
    >
      <CalendarDndProvider
        onEventUpdate={handleEventUpdate}
        timezone={timezone}
        timeFormat={timeFormat}
      >
        <div
          className={cn(
            "flex items-center justify-between p-3 px-6 sm:px-6 sm:p-6",
            className
          )}
        >
          <div className="flex items-center gap-1 sm:gap-4">
            {onToggleSidebar && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="hidden md:inline-flex"
                onClick={onToggleSidebar}
                aria-label={
                  isSidebarOpen
                    ? "Hide calendar sidebar"
                    : "Show calendar sidebar"
                }
              >
                {isSidebarOpen ? (
                  <PanelLeftClose size={16} aria-hidden="true" />
                ) : (
                  <PanelLeftOpen size={16} aria-hidden="true" />
                )}
              </Button>
            )}
            {onToggleSidebar && (
              <Button
                type="button"
                variant="outline"
                className="md:hidden"
                onClick={onToggleSidebar}
              >
                {isSidebarOpen ? (
                  <PanelLeftClose
                    size={16}
                    aria-hidden="true"
                    className="mr-1"
                  />
                ) : (
                  <PanelLeftOpen
                    size={16}
                    aria-hidden="true"
                    className="mr-1"
                  />
                )}
                {isSidebarOpen ? "Close" : "Calendars"}
              </Button>
            )}
            <Button
              variant="outline"
              className="max-[479px]:aspect-square max-[479px]:p-0"
              onClick={handleToday}
            >
              <RiCalendarCheckLine
                className="min-[480px]:hidden"
                size={16}
                aria-hidden="true"
              />

              <span className="">Today</span>
            </Button>
            <div className="flex items-center sm:gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevious}
                aria-label="Previous"
              >
                <ChevronLeftIcon size={16} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNext}
                aria-label="Next"
              >
                <ChevronRightIcon size={16} aria-hidden="true" />
              </Button>
            </div>
            <h2 className="text-sm font-semibold sm:text-lg md:text-xl">
              {viewTitle}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1.5 max-[479px]:h-8">
                  <span>
                    <span className="min-[480px]:hidden" aria-hidden="true">
                      {view.charAt(0).toUpperCase()}
                    </span>
                    <span className="max-[479px]:sr-only">
                      {view.charAt(0).toUpperCase() + view.slice(1)}
                    </span>
                  </span>
                  <ChevronDownIcon
                    className="-me-1 opacity-60"
                    size={16}
                    aria-hidden="true"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-32">
                <DropdownMenuItem onClick={() => updateView("month")}>
                  Month <DropdownMenuShortcut>M</DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateView("week")}>
                  Week <DropdownMenuShortcut>W</DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateView("day")}>
                  Day <DropdownMenuShortcut>D</DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => updateView("agenda")}>
                  Agenda <DropdownMenuShortcut>A</DropdownMenuShortcut>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {view === "day" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-1.5 max-[479px]:h-8">
                    <span>
                      {
                        DAY_GROUPING_OPTIONS.find(
                          (option) => option.value === dayGrouping
                        )?.label || "Doctors"
                      }
                    </span>
                    <ChevronDownIcon
                      className="-me-1 opacity-60"
                      size={16}
                      aria-hidden="true"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-40">
                  {DAY_GROUPING_OPTIONS.map((option) => {
                    const isDisabled =
                      option.value === "chairs" && chairs.length <= 1;
                    return (
                      <DropdownMenuItem
                        key={option.value}
                        disabled={isDisabled}
                        onClick={() =>
                          !isDisabled && setDayGrouping(option.value)
                        }
                      >
                        {option.label}
                        <DropdownMenuShortcut>
                          {option.shortcut}
                        </DropdownMenuShortcut>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              className="max-[479px]:aspect-square max-[479px]:p-0"
              onClick={() => {
                setSelectedEvent(null);
                setIsEventDialogOpen(true);
              }}
            >
              <PlusIcon className="opacity-80" size={16} aria-hidden="true" />
              <span className="max-sm:sr-only">New event</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        <div className="flex flex-1 flex-col">
          {view === "month" && (
            <MonthView
              currentDate={currentDate}
              events={events}
              onEventSelect={handleEventSelect}
              onEventCreate={handleEventCreate}
              timezone={timezone}
              timeFormat={timeFormat}
            />
          )}
          {view === "week" && (
            <WeekView
              currentDate={currentDate}
              events={events}
              onEventSelect={handleEventSelect}
              onEventCreate={handleEventCreate}
              timezone={timezone}
              timeFormat={timeFormat}
              weekStartsOn={weekStartsOn}
              businessHours={businessHours}
            />
          )}
          {view === "day" && (
            <DayView
              currentDate={currentDate}
              events={events}
              onEventSelect={handleEventSelect}
              onEventCreate={handleEventCreate}
              timezone={timezone}
              timeFormat={timeFormat}
              businessHours={businessHours}
              resources={dayResources}
              resourceMode={dayGrouping}
            />
          )}
          {view === "agenda" && (
            <AgendaView
              currentDate={currentDate}
              events={events}
              onEventSelect={handleEventSelect}
              timezone={timezone}
              timeFormat={timeFormat}
            />
          )}
        </div>

        <EventDialog
          event={selectedEvent}
          isOpen={isEventDialogOpen}
          onClose={() => {
            setIsEventDialogOpen(false);
            setSelectedEvent(null);
          }}
          onSave={handleEventSave}
          onDelete={handleEventDelete}
          calendars={calendars}
        />
      </CalendarDndProvider>
    </div>
  );
}
